import { SEOContentFAQ } from "../../../../components/seo/SEOContentFAQ";
import { SEOContentHowTo } from "../../../../components/seo/SEOContentHowTo";
import { SEOImage } from "../../../../components/seo/SEOImage";

export const TeleprompterTikTokShortsES = () => (
    <>
        <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Teleprompter para TikTok y Reels: Graba Videos Virales Sin Perder una Palabra
        </h2>

        <p className="lead text-xl text-slate-300 mb-8 font-medium">
            En el mundo acelerado de TikTok, Reels y YouTube Shorts, la retención lo es todo. Solo tienes unos segundos para captar la atención de la audiencia, pero tartamudear o mirar hacia otro lado puede arruinar tu video. <strong>La pregunta es: ¿quieres pasar horas memorizando líneas o minutos produciendo éxitos?</strong>
        </p>

        <p className="text-slate-300 mb-8">
            En esta guía, exploraremos por qué a los mejores creadores les encanta <strong>PromptNinja</strong>. Descubre cómo nuestro teleprompter flotante te permite mantener el ritmo, la energía y la conexión visual impecable, transformando guiones complejos en videos virales de forma sencilla y profesional. ¡Prepárate para multiplicar tu producción y dominar el algoritmo con total fluidez!
        </p>

        <SEOImage
            slug="teleprompter-tiktok-shorts"
            src="teleprompter-tiktok-reels-vertical.webp"
            alt="Creador de contenido grabando video vertical para TikTok con un teleprompter"
            caption="El teleprompter vertical de PromptNinja está optimizado para las proporciones de TikTok e Instagram Reels."
            width={720}
            height={1280}
            priority={true}
        />

        <div className="bg-slate-900/50 p-6 md:p-8 rounded-2xl border border-slate-800 shadow-xl mb-12">
            <h3 className="text-2xl font-bold text-white mb-6">¿Por qué PromptNinja es Perfecto para tus TikToks y Shorts?</h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <li className="flex items-start gap-3 bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                    <span className="text-blue-400 text-xl">🎬</span>
                    <div className="text-sm">
                        <strong className="text-white block mb-1">Fluidez Sin Cortes</strong>
                        <span className="text-slate-400">Olvida los "ehhh..." y "ems...". Tu guion se desplaza a tu ritmo, asegurando un discurso natural y continuo.</span>
                    </div>
                </li>
                <li className="flex items-start gap-3 bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                    <span className="text-blue-400 text-xl">👁️</span>
                    <div className="text-sm">
                        <strong className="text-white block mb-1">Contacto Visual Impecable</strong>
                        <span className="text-slate-400">Mira directamente a la lente de tu smartphone, creando una conexión auténtica con tu audiencia.</span>
                    </div>
                </li>
                <li className="flex items-start gap-3 bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                    <span className="text-blue-400 text-xl">⏱️</span>
                    <div className="text-sm">
                        <strong className="text-white block mb-1">Ahorro de Tiempo Precioso</strong>
                        <span className="text-slate-400">Graba en menos tomas y edita menos. Más tiempo para crear, menos tiempo para corregir errores.</span>
                    </div>
                </li>
                <li className="flex items-start gap-3 bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                    <span className="text-blue-400 text-xl">🏆</span>
                    <div className="text-sm">
                        <strong className="text-white block mb-1">Profesionalismo Instantáneo</strong>
                        <span className="text-slate-400">Videos más pulidos y bien articulados aumentan el compromiso y tu autoridad percibida.</span>
                    </div>
                </li>
            </ul>
            <p className="text-slate-400 text-center mt-6 text-sm italic">
                ¿Listo para transformar tus videos cortos y capturar la atención de tu audiencia? Sigue leyendo para descubrir cómo.
            </p>
        </div>

        <section className="my-12 p-8 bg-purple-900/20 rounded-2xl border border-purple-500/30 shadow-2xl">
            <h2 className="text-3xl font-bold text-center text-white mb-10">Graba tus TikToks y Shorts en 3 Pasos Simples</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center group">
                    <div className="bg-slate-900 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border border-purple-500/50 group-hover:scale-110 transition-transform shadow-lg shadow-purple-500/20">
                        <span className="text-2xl">📝</span>
                    </div>
                    <h3 className="font-bold text-white text-xl mb-2">1. Crea tu Guion</h3>
                    <p className="text-slate-400 text-sm">Escribe tu guion en PromptNinja, enfocándote en mensajes cortos y de alto impacto ideales para el ritmo de TikTok.</p>
                </div>
                <div className="text-center group">
                    <div className="bg-slate-900 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border border-purple-500/50 group-hover:scale-110 transition-transform shadow-lg shadow-purple-500/20">
                        <span className="text-2xl">📱</span>
                    </div>
                    <h3 className="font-bold text-white text-xl mb-2">2. Posiciona la Cámara</h3>
                    <p className="text-slate-400 text-sm">Usa tu smartphone verticalmente. PromptNinja se adapta perfectamente al formato 9:16 de TikTok y Shorts.</p>
                </div>
                <div className="text-center group">
                    <div className="bg-slate-900 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border border-purple-500/50 group-hover:scale-110 transition-transform shadow-lg shadow-purple-500/20">
                        <span className="text-2xl">🔴</span>
                    </div>
                    <h3 className="font-bold text-white text-xl mb-2">3. Graba con Confianza</h3>
                    <p className="text-slate-400 text-sm">Lee tu texto mientras mantienes contacto visual. ¡Haz clic en grabar y tu video está listo para brillar!</p>
                </div>
            </div>
        </section>

        <div className="bg-gradient-to-r from-red-900/30 to-pink-900/30 p-8 rounded-xl border border-pink-500/30 my-12">
            <h3 className="text-3xl font-bold text-white mb-6 text-center">La Trampa del Creador de Videos Cortos</h3>
            <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-slate-900/50 p-6 rounded-lg border border-red-500/30">
                    <h3 className="font-bold text-red-400 mb-4 flex items-center gap-2">
                        <span>❌</span> SIN Teleprompter (Memorizando)
                    </h3>
                    <ul className="space-y-3 text-slate-300 text-sm">
                        <li>📝 <strong>Preparación:</strong> 20min intentando memorizar 15 líneas</li>
                        <li>🎬 <strong>Grabación:</strong> 40min para grabar 15s (20-30 tomas)</li>
                        <li>😰 <strong>Retención:</strong> 15-30% (la gente te ve tropezar y se va)</li>
                        <li>💬 <strong>Comentarios:</strong> "Parece nervioso", "Lee mejor el guion"</li>
                        <li>📊 <strong>Resultados:</strong> 2-3 videos por día (máximo)</li>
                        <li>🔥 <strong>Desgaste:</strong> Alto. Se rinde en 2 semanas</li>
                    </ul>
                </div>
                <div className="bg-slate-900/50 p-6 rounded-lg border border-green-500/30">
                    <h3 className="font-bold text-green-400 mb-4 flex items-center gap-2">
                        <span>✅</span> CON PromptNinja (Leyendo Naturalmente)
                    </h3>
                    <ul className="space-y-3 text-slate-300 text-sm">
                        <li>📝 <strong>Preparación:</strong> 2min pegando texto en el teleprompter</li>
                        <li>🎬 <strong>Grabación:</strong> 10min para grabar 15s (2-3 tomas)</li>
                        <li>😊 <strong>Retención:</strong> 50-70% (entrega fluida y segura)</li>
                        <li>💬 <strong>Comentarios:</strong> "¡Wow, qué confianza!", "Contenido top"</li>
                        <li>📊 <strong>Resultados:</strong> 10-15 videos por día fácilmente</li>
                        <li>🚀 <strong>Desgaste:</strong> Bajo. Sostenible a largo plazo</li>
                    </ul>
                </div>
            </div>
            <p className="text-slate-400 text-sm mt-6 italic text-center">
                💡 Productividad: <strong className="text-green-400">5x más videos</strong> en el mismo tiempo. Calidad: <strong className="text-green-400">conexión visual mantenida</strong>.
            </p>
        </div>

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">¿Por qué los Teleprompters de Escritorio No Funcionan para Shorts?</h3>
        <p className="mb-6 leading-relaxed">
            Muchos creadores intentan usar soluciones hechas para videos horizontales de YouTube en contenido vertical. ¿El resultado? Un desastre.
        </p>

        <SEOImage
            slug="teleprompter-tiktok-shorts"
            src="teleprompter-tiktok-close-up-interface.webp"
            alt="Primer plano de la interfaz del teleprompter PromptNinja en un smartphone"
            caption="La interfaz limpia y enfocada de PromptNinja permite una lectura fluida incluso en las pantallas pequeñas de los teléfonos inteligentes."
            width={720}
            height={1280}
        />

        <div className="bg-slate-800 p-6 rounded-lg my-8">
            <h3 className="text-xl font-bold text-white mb-4">Problemas al Usar Teleprompter Horizontal para Video Vertical:</h3>
            <ul className="space-y-3 text-slate-300">
                <li className="flex items-start gap-3">
                    <span className="text-red-400 font-bold">1.</span>
                    <div>
                        <strong>Cámara arriba, texto abajo:</strong> Terminas mirando hacia abajo, pareciendo desconectado o inseguro. En video vertical, la cámara frontal está ARRIBA, no enfrente.
                    </div>
                </li>
                <li className="flex items-start gap-3">
                    <span className="text-red-400 font-bold">2.</span>
                    <div>
                        <strong>Texto horizontal en pantalla vertical:</strong> Solo caben 3-4 palabras por línea. Tus ojos hacen ping-pong de izquierda a derecha, haciendo obvio que estás leyendo.
                    </div>
                </li>
                <li className="flex items-start gap-3">
                    <span className="text-red-400 font-bold">3.</span>
                    <div>
                        <strong>Control difícil:</strong> Con el teléfono en modo de grabación vertical, no puedes tocarlo para pausar sin que tiemble. Necesita control remoto.
                    </div>
                </li>
            </ul>
        </div>

        <div className="bg-slate-800 p-6 rounded-lg my-12 border-l-4 border-pink-500">
            <h3 className="text-2xl font-bold text-white mb-4">PromptNinja: Hecho a Medida para Móvil</h3>
            <p className="text-slate-300 mb-4">
                Diseñamos esto específicamente para creadores móviles. Mira las diferencias:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-slate-900 p-4 rounded-lg">
                    <h4 className="font-bold text-pink-400 mb-2">📱 Interfaz Vertical Nativa</h4>
                    <p className="text-sm text-slate-400">Cuando se abre en móvil, la interfaz se adapta automáticamente al modo retrato. El texto fluye de arriba a abajo, siguiendo tu lectura natural.</p>
                </div>
                <div className="bg-slate-900 p-4 rounded-lg">
                    <h4 className="font-bold text-pink-400 mb-2">🎮 Control P2P Sin Lag</h4>
                    <p className="text-sm text-slate-400">Usa otro teléfono o portátil como control remoto. Conexión directa vía Wi-Fi local, cero dependencia de internet. 50ms de latencia.</p>
                </div>
                <div className="bg-slate-900 p-4 rounded-lg">
                    <h4 className="font-bold text-pink-400 mb-2">🎙️ Control por Voz (Pro)</h4>
                    <p className="text-sm text-slate-400">El texto avanza mientras hablas. Manos libres para gesticular. Se siente como si hablaras desde el corazón, no leyendo.</p>
                </div>
                <div className="bg-slate-900 p-4 rounded-lg">
                    <h4 className="font-bold text-pink-400 mb-2">⚡ Instalable como App</h4>
                    <p className="text-sm text-slate-400">PWA = acceso directo en pantalla de inicio. Funciona offline. Sin GBs de memoria usados. Es una app sin ser una app.</p>
                </div>
            </div>
        </div>

        <SEOContentHowTo
            title="Flujo de Trabajo Perfecto: Graba 10 Shorts en 1 Hora"
            schemaTitle="Cómo Grabar Videos Verticales con Teleprompter"
            totalTime="PT60M"
            tools={["Smartphone con trípode", "PromptNinja", "Segundo dispositivo (opcional)"]}
            steps={[
                {
                    title: "Paso 1: Preparar Guiones en Lotes (15min)",
                    text: "Escribe 10 guiones de 30-60s cada uno. Pégalos todos en PromptNinja. Pon en negrita las palabras clave para recordar dónde enfatizar."
                },
                {
                    title: "Paso 2: Configuración Física (5min)",
                    text: "Trípode con teléfono vertical. Ajusta la altura para que la cámara esté a la altura de los ojos. Abre PromptNinja y posiciona el texto en la parte SUPERIOR de la pantalla, pegado a la cámara."
                },
                {
                    title: "Paso 3: Grabación Secuencial (30min)",
                    text: "Graba video 1, pausa 10 segundos, cambia texto, graba video 2. No edites todavía. Solo graba."
                },
                {
                    title: "Paso 4: Edición por Lotes en CapCut (10min)",
                    text: "Importa los 10 videos en CapCut. Aplica la misma plantilla: Subtítulos Auto + música tendencia + transiciones. Como el crudo está limpio, es solo copiar/pegar efectos."
                }
            ]}
        />

        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-8 rounded-2xl my-16 text-center shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
                <div className="absolute top-[-10%] right-[-10%] w-64 h-64 bg-white rounded-full blur-3xl"></div>
                <div className="absolute bottom-[-10%] left-[-10%] w-64 h-64 bg-blue-400 rounded-full blur-3xl"></div>
            </div>
            <h3 className="text-3xl font-extrabold text-white mb-4 leading-tight relative z-10">🚨 ¡No Más Retomas! ¡Empieza a Crear TikToks Increíbles Hoy!</h3>
            <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto relative z-10">PromptNinja es la herramienta secreta para creadores de contenido que valoran el tiempo y la calidad. ¡Pruébalo ahora y siente la diferencia!</p>
            <a
                href="/?lang=es#app"
                className="inline-block bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-bold py-4 px-10 rounded-full text-xl uppercase tracking-wide shadow-xl transform hover:scale-105 transition-all duration-300 relative z-10"
            >
                Prueba PromptNinja Gratis
            </a>
        </div>

        <h3 className="text-3xl font-bold text-white mt-16 mb-6">Configuración del Creador: De Cero a Pro</h3>
        <p className="text-slate-300 mb-8">
            No necesitas equipo costoso. Mira los 3 niveles:
        </p>

        <div className="space-y-6 mb-12">
            <div className="bg-slate-900 p-6 rounded-xl border-l-4 border-slate-700">
                <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl">🔴</span>
                    <h3 className="text-2xl font-bold text-white">Nivel 1: Principiante (1 Teléfono)</h3>
                </div>
                <p className="text-slate-300 mb-4">
                    <strong>Configuración:</strong> Solo el teléfono. Usa botones de volumen para grabar para evitar sacudidas.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <p className="text-green-400 text-sm mb-2"><strong>✅ Ventaja:</strong></p>
                        <ul className="text-slate-400 text-sm space-y-1 pl-4">
                            <li>Cero inversión</li>
                            <li>Empieza hoy</li>
                            <li>Aprende lo básico</li>
                        </ul>
                    </div>
                    <div>
                        <p className="text-yellow-400 text-sm mb-2"><strong>⚠️ Limitación:</strong></p>
                        <ul className="text-slate-400 text-sm space-y-1 pl-4">
                            <li>Video inestable a mano alzada</li>
                            <li>Más difícil mantener el foco</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="bg-slate-900 p-6 rounded-xl border-l-4 border-yellow-600">
                <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl">🟡</span>
                    <h3 className="text-2xl font-bold text-white">Nivel 2: Intermedio (Trípode + Remoto)</h3>
                </div>
                <p className="text-slate-300 mb-4">
                    <strong>Configuración:</strong> Trípode de teléfono + segundo dispositivo como control remoto P2P.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <p className="text-green-400 text-sm mb-2"><strong>✅ Ventaja:</strong></p>
                        <ul className="text-slate-400 text-sm space-y-1 pl-4">
                            <li>Cámara 100% estable</li>
                            <li>Controla el texto sin tocar el teléfono</li>
                            <li>Graba por lotes</li>
                        </ul>
                    </div>
                    <div>
                        <p className="text-yellow-400 text-sm mb-2"><strong>💡 Ideal para:</strong></p>
                        <ul className="text-slate-400 text-sm space-y-1 pl-4">
                            <li>Publicadores diarios</li>
                            <li>Grabación por lotes</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="bg-slate-900 p-6 rounded-xl border-l-4 border-green-600">
                <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl">🟢</span>
                    <h3 className="text-2xl font-bold text-white">Nivel 3: Profesional (Control por Voz)</h3>
                </div>
                <p className="text-slate-300 mb-4">
                    <strong>Configuración:</strong> PromptNinja PRO + Trípode + Aro de Luz + Micrófono de solapa (opcional). Control por voz activado.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <p className="text-green-400 text-sm mb-2"><strong>✅ Ventaja:</strong></p>
                        <ul className="text-slate-400 text-sm space-y-1 pl-4">
                            <li>100% manos libres para gesticular</li>
                            <li>El texto se sincroniza con TU voz</li>
                            <li>Flujo natural (parece improvisado)</li>
                            <li>Producción profesional en casa</li>
                        </ul>
                    </div>
                    <div>
                        <p className="text-purple-400 text-sm mb-2"><strong>🎯 Ideal para:</strong></p>
                        <ul className="text-slate-400 text-sm space-y-1 pl-4">
                            <li>Creadores a tiempo completo (1M+ seguidores)</li>
                            <li>Videos educativos largos</li>
                            <li>Necesitan máxima naturalidad</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">Consejos de Expertos para Videos Verticales Virales</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
                <h3 className="font-bold text-pink-400 mb-3 text-xl">🎯 Gancho en los Primeros 1.5s</h3>
                <p className="text-slate-300 text-sm mb-3">
                    Coloca tu frase de alto impacto en la parte superior del guion. Resáltala en rojo para recordar poner energía extra.
                </p>
            </div>
            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
                <h3 className="font-bold text-pink-400 mb-3 text-xl">⏱️ Pausas Estratégicas</h3>
                <p className="text-slate-300 text-sm mb-3">
                    Los videos virales tienen RITMO. Usa dobles Enters o [...] para recordarte hacer una pausa y crear expectativa.
                </p>
            </div>
        </div>

        <h3 className="text-3xl font-bold text-white mt-16 mb-6">Caso Real: De 500 a 50k Seguidores en 3 Meses</h3>
        <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 p-8 rounded-xl border border-purple-500/30 mb-12">
            <div className="bg-slate-900/50 p-6 rounded-lg">
                <h3 className="font-bold text-green-400 mb-3">Resultados en 90 Días:</h3>
                <ul className="space-y-2 text-slate-300 text-sm">
                    <li>📈 Seguidores: <strong>500 → 52,000</strong> (+10,300% crecimiento)</li>
                    <li>🎬 Videos publicados: <strong>84 videos</strong> (vs 12 antes)</li>
                    <li>⏱️ Tiempo de producción: <strong>3h/semana</strong> vs 6h antes (doble producción, mitad de tiempo)</li>
                    <li>📊 Retención promedio: <strong>68%</strong> (vs 35% antes)</li>
                    <li>🔥 Virales: <strong>7 videos superaron 1M de vistas</strong></li>
                </ul>
            </div>

            <p className="text-slate-400 text-sm mt-6 italic">
                💡 Secreto: Consistencia algorítmica (2x/día) + calidad (sin tartamudeo) = crecimiento exponencial.
            </p>

            <p className="text-slate-500 text-xs mt-4">
                *Caso compartido con autorización. Los resultados individuales varían según el nicho, calidad del contenido y dedicación.
            </p>
        </div>

        <div className="text-center mt-12 mb-12">
            <a
                href="/?lang=es#app"
                className="inline-block bg-gradient-to-r from-pink-500 to-purple-600 font-bold py-4 px-10 rounded-full transition hover:brightness-110 hover:scale-105 transform shadow-lg text-lg"
                style={{ color: 'white' }}
            >
                Triplica tu Producción de Video Ahora (Gratis)
            </a>
            <p className="text-slate-400 mt-4 text-sm">Sin instalación. Sin tarjeta de crédito. Empieza a grabar en 30 segundos.</p>
        </div>

        <SEOContentFAQ
            title="Preguntas Frecuentes: Teleprompter para Videos Verticales"
            items={[
                {
                    question: "¿Cómo evito parecer que estoy leyendo?",
                    answer: "Tres secretos: (1) Texto SIEMPRE arriba de la pantalla, pegado a la cámara frontal. (2) Fuente más pequeña (28-36px) y tú más lejos (~70cm). (3) Practica hablar con ENERGÍA incluso leyendo. El problema no es leer, es leer sin energía."
                },
                {
                    question: "¿Necesito descargar una app?",
                    answer: "¡No! PromptNinja es 100% web. Funciona directamente en Chrome/Safari en móvil. Puedes crear un acceso directo (PWA) para que parezca una app nativa, pero no necesitas bajar nada de la tienda."
                },
                {
                    question: "¿Cómo grabo sin tocar el teléfono y moverlo?",
                    answer: "Dos soluciones: (1) Usa otro teléfono/portátil como control remoto vía código QR (gratis). (2) Usa control por voz (Pro) - el texto se mueve al hablar, 100% manos libres."
                },
                {
                    question: "¿Funciona para Instagram Stories y Reels?",
                    answer: "¡Sí! La interfaz vertical de PromptNinja funciona perfectamente en CUALQUIER plataforma de video vertical: TikTok, Instagram Reels, Instagram Stories, YouTube Shorts, Kwai, Snapchat Spotlight. El formato 9:16 es universal."
                },
                {
                    question: "¿Puedo usar con aro de luz, micrófono y otros equipos?",
                    answer: "Absolutamente. PromptNinja no interfiere con nada. Configura tu equipo normalmente (trípode, aro de luz, micro de solapa, pantalla verde, lo que uses) y solo añade el teleprompter como último paso."
                },
                {
                    question: "¿Cuánto tiempo toma grabar 10 Shorts con teleprompter?",
                    answer: "Con práctica: 1h para 10 videos de 30s cada uno. Desglose: 10min preparando guiones en PromptNinja, 5min configurando setup físico, 30min grabando (3min por video, 2 tomas), 15min editando en CapCut aplicando misma plantilla. Grabar por lotes es EL secreto."
                },
                {
                    question: "¿Hay límite de palabras o tiempo?",
                    answer: "Cero límite. Pega guiones de 10 palabras o 10,000 palabras. PromptNinja lo maneja. Obviamente para Shorts querrás guiones cortos (30-90 palabras), pero técnicamente no hay techo."
                },
                {
                    question: "¿Funciona offline después de instalar?",
                    answer: "Sí. Instala como PWA (acceso directo) y PromptNinja funciona 100% offline. Tus guiones se guardan localmente. Solo necesitas internet si usas control remoto P2P (que usa Wi-Fi local, no internet externo)."
                }
            ]}
        />

        <div className="bg-gradient-to-r from-pink-900/20 to-purple-900/20 p-8 rounded-xl border border-pink-500/30 text-center my-12">
            <h3 className="text-3xl font-bold text-white mb-4">Tu Era de Creador Constante Comienza Ahora</h3>
            <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                Deja de luchar con tu memoria. Deja de gastar 40 minutos para grabar 15 segundos. Empieza a producir como los grandes canales: con sistema, consistencia y resultados.
            </p>
            <a
                href="/?lang=es#app"
                className="inline-block bg-gradient-to-r from-pink-500 to-purple-500 font-bold py-4 px-10 rounded-full transition hover:brightness-110 hover:scale-105 transform shadow-lg text-lg"
                style={{ color: 'white' }}
            >
                Empieza a Grabar Ahora (100% Gratis) →
            </a>
        </div>
    </>
);
